import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  RefreshCw,
  X,
  Plus,
  ShoppingCart,
  MoreVertical,
  Eye,
  Pencil,
  FileText,
  Trash2,
  Calendar,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  FileSpreadsheet,
  Copy,
  Globe,
} from 'lucide-react';
import quotationService from '../services/quotationService';
import excelExportService from '../services/excelExportService';
import TemplateSelector from '../components/TemplateSelector';
import authService from '../services/authService';
import useFilterPersistence from '../hooks/useFilterPersistence';
import Layout from '../components/Layout';
import { useColumnManager } from '../hooks/useColumnManager';
import SalesImportDialog from '../components/SalesImportDialog';

const QuotationPage = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get current column preferences for export
  const { currentColumns } = useColumnManager('quotation');

  // Calculate default date range (last 30 days)
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Use filter persistence hook
  const [persistedState, setPersistedState] = useFilterPersistence('quotations', {
    filters: {
      quotation_id: '',
      client_name: '',
      status: ''
    },
    dateRange: getDefaultDateRange(),
    pagination: {
      page: 1,
      page_size: 10,
      total_count: 0,
      total_pages: 0
    },
    sorting: {
      orderBy: 'created_at',
      order: 'desc'
    },
    tradeTypeFilter: 'all',
    clientTypeFilter: 'domestic'
  });

  // Extract state from persisted state
  const filters = persistedState.filters;
  const dateRange = persistedState.dateRange;
  const pagination = persistedState.pagination;
  const sorting = persistedState.sorting;
  const tradeTypeFilter = persistedState.tradeTypeFilter;
  const clientTypeFilter = persistedState.clientTypeFilter;

  // Update functions
  const setFilters = (newFilters) => {
    setPersistedState(prev => ({
      ...prev,
      filters: typeof newFilters === 'function' ? newFilters(prev.filters) : newFilters
    }));
  };

  const setDateRange = (newDateRange) => {
    setPersistedState(prev => ({
      ...prev,
      dateRange: typeof newDateRange === 'function' ? newDateRange(prev.dateRange) : newDateRange
    }));
  };

  const setPagination = useCallback((newPagination) => {
    setPersistedState(prev => ({
      ...prev,
      pagination: typeof newPagination === 'function' ? newPagination(prev.pagination) : newPagination
    }));
  }, [setPersistedState]);

  const setSorting = (newSorting) => {
    setPersistedState(prev => ({
      ...prev,
      sorting: typeof newSorting === 'function' ? newSorting(prev.sorting) : newSorting
    }));
  };

  const setTradeTypeFilter = (newTradeType) => {
    setPersistedState(prev => ({
      ...prev,
      tradeTypeFilter: typeof newTradeType === 'function' ? newTradeType(prev.tradeTypeFilter) : newTradeType
    }));
  };

  const setClientTypeFilter = (newClientType) => {
    setPersistedState(prev => ({
      ...prev,
      clientTypeFilter: typeof newClientType === 'function' ? newClientType(prev.clientTypeFilter) : newClientType
    }));
  };

  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [pendingExportQuotation, setPendingExportQuotation] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, quotation: null, action: '' });
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // User role check for admin features
  const [userRole, setUserRole] = useState('');
  const canViewPrices = authService.canViewQuotationPrices();

  const loadQuotations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await quotationService.getQuotations(
        pagination.page,
        pagination.page_size,
        {
          ...filters,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          sort_by: sorting.orderBy,
          sort_order: sorting.order,
          client_type: clientTypeFilter
        }
      );

      // Handle different response structures
      if (response.quotations && Array.isArray(response.quotations)) {
        setQuotations(response.quotations);
      } else if (Array.isArray(response)) {
        setQuotations(response);
      } else {
        console.error('Unexpected response structure:', response);
        setQuotations([]);
      }

      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          ...response.pagination
        }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, filters, sorting, dateRange, clientTypeFilter, setPagination]);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  // Load user role on component mount
  useEffect(() => {
    const user = authService.getCurrentUser();
    setUserRole(user?.role || '');
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterSubmit = (e) => {
    if (e) e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadQuotations();
  };

  const handleClearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      quotation_id: '',
      client_name: '',
      status: ''
    });
    setDateRange(getDefaultDateRange());
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (property) => {
    const isAsc = sorting.orderBy === property && sorting.order === 'asc';
    setSorting({
      orderBy: property,
      order: isAsc ? 'desc' : 'asc'
    });
  };

  const handleAction = (action, quotation) => {
    if (action === 'view') {
      navigate(`/quotations/${quotation._id}/view`, { state: { quotationData: quotation } });
    } else if (action === 'edit') {
      navigate(`/quotations/${quotation._id}/edit`);
    } else if (action === 'clone') {
      const clonedQuotation = {
        ...quotation,
        _id: undefined,
        quotation_id: undefined,
        status: 'draft',
        created_at: undefined,
        updated_at: undefined,
        products: quotation.products?.map(p => ({
          ...p,
          _id: undefined,
          line_id: undefined,
          matched_product_id: undefined,
          product_mongo_id: p._id || p.product_mongo_id || p.matched_product_id
        })) || []
      };
      navigate('/quotations/new', { state: { clonedQuotation } });
    } else if (action === 'convert') {
      setConfirmDialog({ open: true, quotation, action: 'convert' });
    } else if (action === 'delete') {
      setConfirmDialog({ open: true, quotation, action: 'delete' });
    } else if (action === 'export') {
      setPendingExportQuotation(quotation);
      setTemplateSelectorOpen(true);
    }
  };

  const handleConfirmAction = async () => {
    const { quotation, action } = confirmDialog;
    setConfirmDialog({ open: false, quotation: null, action: '' });

    try {
      if (action === 'convert') {
        await quotationService.convertQuotationToOrder(quotation._id);
        setQuotations(prev => prev.filter(q => q._id !== quotation._id));
        alert('Quotation converted to sales order successfully! The original quotation has been removed.');
      } else if (action === 'delete') {
        await quotationService.deleteQuotation(quotation._id);
        setQuotations(prev => prev.filter(q => q._id !== quotation._id));
        alert('Quotation deleted successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} quotation`);
    }
  };

  const handleExport = async (quotation, templateId = null) => {
    try {
      setLoading(true);
      if (templateId) {
        const { blob, filename } = await excelExportService.exportUsingTemplate('quotation', {
          template_id: templateId,
          quotation_ids: [quotation._id],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `quotation_${quotation.quotation_id || quotation._id}.xlsx`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        await quotationService.exportQuotation(quotation._id, currentColumns);
      }
      setError('');
    } catch (err) {
      console.error('Export error:', err);
      let errorMessage = 'Failed to export quotation';

      if (err.response?.status === 404) {
        errorMessage = 'Quotation not found';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to export quotations';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelected = async (template) => {
    setTemplateSelectorOpen(false);
    if (pendingExportQuotation) {
      await handleExport(pendingExportQuotation, template?.template_id);
      setPendingExportQuotation(null);
    }
  };

  const handleTemplateSelectorClose = () => {
    setTemplateSelectorOpen(false);
    setPendingExportQuotation(null);
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'outline';
      case 'quoted': return 'secondary';
      case 'confirmed': return 'default';
      case 'converted': return 'default';
      default: return 'outline';
    }
  };

  const getStatusLabel = (status) => {
    const value = (status || '').toLowerCase();
    switch (value) {
      case 'draft': return '草稿';
      case 'pending': return '待处理';
      case 'quoted': return '已报价';
      case 'confirmed': return '已确认';
      case 'completed':
      case 'converted': return '已转换';
      default: return status || '待处理';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Fallback: compute totals from product lines when document-level totals are missing
  const calculateLineItemTotals = (quotation) => {
    const products = quotation?.products || [];
    const subtotalExcl = products.reduce((sum, p) => sum + (parseFloat(p.total_price) || 0), 0);
    const subtotalIncl = products.reduce((sum, p) => sum + (parseFloat(p.total_price_tax_included) || 0), 0);
    return { subtotalExcl, subtotalIncl };
  };


  const headCells = useMemo(() => {
    const cells = [
      { id: 'quotation_id', label: '报价单编号' },
      { id: 'client_name', label: '客户名称' },
      { id: 'status', label: '状态' },
      { id: 'total_products', label: '产品数量' },
    ];
    if (canViewPrices) {
      cells.push(
        { id: 'total_price', label: '未税总价' },
        { id: 'total_with_tax', label: '合计（含 13% 税）', sortable: false }
      );
    }
    cells.push(
      { id: 'date', label: '日期' },
      { id: 'created_at', label: '创建日期' },
      { id: 'actions', label: '操作', sortable: false }
    );
    return cells;
  }, [canViewPrices]);


  const SortIcon = ({ columnId }) => {
    if (sorting.orderBy !== columnId) return <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />;
    return sorting.order === 'asc'
      ? <ArrowUp className="ml-1 h-4 w-4" />
      : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  return (
    <>
      <Layout title="报价管理" activeTab="quotations">
        <div className="container mx-auto px-4 py-6 max-w-7xl">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-foreground">报价单</h1>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setImportDialogOpen(true)}
                disabled={loading}
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                从文件导入
              </Button>
              <Button onClick={() => navigate('/quotations/new')}>
                <Plus className="mr-2 h-4 w-4" />
                新建报价单
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      name="quotation_id"
                      placeholder="按报价单编号筛选..."
                      value={filters.quotation_id}
                      onChange={handleFilterChange}
                      className="pl-10 pr-8"
                    />
                    {filters.quotation_id && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => handleClearFilter('quotation_id')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      name="client_name"
                      placeholder="按客户名称筛选..."
                      value={filters.client_name}
                      onChange={handleFilterChange}
                      className="pl-10 pr-8"
                    />
                    {filters.client_name && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => handleClearFilter('client_name')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="min-w-[150px]">
                  <div className="relative">
                    <Input
                      name="status"
                      placeholder="按状态筛选..."
                      value={filters.status}
                      onChange={handleFilterChange}
                      className="pr-8"
                    />
                    {filters.status && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => handleClearFilter('status')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="h-8 w-px bg-border mx-1" />

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div className="space-y-1">
                    <Label className="text-xs">开始日期</Label>
                    <Input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                      className="h-9 w-[140px]"
                    />
                  </div>
                  <span className="text-sm text-muted-foreground">至</span>
                  <div className="space-y-1">
                    <Label className="text-xs">结束日期</Label>
                    <Input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                      className="h-9 w-[140px]"
                    />
                  </div>
                </div>
                <div className="h-8 w-px bg-border mx-1" />

                {/* Client Type Filter - Domestic/Import */}
                <div className="flex items-center gap-3 min-w-[140px]">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="client-type-filter"
                      checked={clientTypeFilter === 'domestic'}
                      onCheckedChange={(checked) => setClientTypeFilter(checked ? 'domestic' : 'import')}
                    />
                    <div className="flex flex-col">
                      <Label htmlFor="client-type-filter" className="text-xs cursor-pointer">
                        {clientTypeFilter === 'domestic' ? '国内客户' : '进口客户'}
                      </Label>
                      <span className="text-[10px] text-muted-foreground">
                        {clientTypeFilter === 'domestic' ? '仅显示国内' : '仅显示进口'}
                      </span>
                    </div>
                  </div>
                  <Globe className={`h-4 w-4 ${clientTypeFilter === 'domestic' ? 'text-blue-500' : 'text-purple-500'}`} />
                </div>
                {userRole === 'admin' && (
                  <>
                    <div className="h-8 w-px bg-border mx-1" />
                    <div className="min-w-[150px]">
                      <Label className="text-xs mb-1 block">贸易类型 (管理员)</Label>
                      <Select value={tradeTypeFilter} onValueChange={setTradeTypeFilter}>
                        <SelectTrigger className="h-9 border-primary">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">全部报价单</SelectItem>
                          <SelectItem value="domestic">仅国内</SelectItem>
                          <SelectItem value="international">仅国际出口</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  variant="ghost"
                  size="icon"
                  disabled={loading}
                  title="刷新"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleClearAllFilters}
                  disabled={loading}
                  title="清除所有筛选"
                >
                  <X className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Quotations Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50 border-b">
                          {headCells.map((headCell) => (
                            <th
                              key={headCell.id}
                              className="px-4 py-3 text-left font-semibold text-foreground"
                            >
                              {headCell.sortable !== false ? (
                                <button
                                  className="flex items-center hover:text-primary transition-colors"
                                  onClick={() => handleSort(headCell.id)}
                                >
                                  {headCell.label}
                                  <SortIcon columnId={headCell.id} />
                                </button>
                              ) : (
                                headCell.label
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {quotations.map((quotation) => (
                          <tr key={quotation._id} className="hover:bg-muted/30">
                            <td className="px-4 py-3">
                              <button
                                className="text-primary underline hover:text-primary/80 transition-colors font-medium"
                                onClick={() => navigate(`/quotations/${quotation._id}/view`)}
                              >
                                {quotation.quotation_id}
                              </button>
                            </td>
                            <td className="px-4 py-3">{quotation.client_name}</td>
                            <td className="px-4 py-3">
                              <Badge variant={getStatusVariant(quotation.status)}>
                                {getStatusLabel(quotation.status)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {(() => {
                                const productLines = quotation.products?.length || quotation.total_products || 0;
                                const totalQuantity = quotation.products?.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0) || 0;
                                return (
                                  <span className="text-muted-foreground">
                                    {productLines}项
                                    {totalQuantity > 0 && (
                                      <span className="text-foreground font-medium ml-1">
                                        -{totalQuantity}支
                                      </span>
                                    )}
                                  </span>
                                );
                              })()}
                            </td>
                            {canViewPrices && (
                              (() => {
                                const lineTotals = calculateLineItemTotals(quotation);
                                return (
                                  <>
                                    <td className="px-4 py-3 font-bold">
                                      {formatCurrency(quotation.subtotal_tax_exclusive || quotation.subtotal || quotation.total_price || lineTotals.subtotalExcl)}
                                    </td>
                                    <td className="px-4 py-3 font-bold text-primary">
                                      {formatCurrency(quotation.subtotal_tax_inclusive || quotation.total_with_tax || lineTotals.subtotalIncl)}
                                    </td>
                                  </>
                                );
                              })()
                            )}

                            <td className="px-4 py-3">
                              {quotation.date
                                ? new Date(quotation.date).toLocaleDateString('zh-CN')
                                : '-'}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {quotation.created_at
                                ? new Date(quotation.created_at).toLocaleDateString('zh-CN')
                                : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleAction('view', quotation)}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    查看详情
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleAction('edit', quotation)}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    编辑报价单
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleAction('clone', quotation)}>
                                    <Copy className="mr-2 h-4 w-4" />
                                    克隆报价单
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleAction('export', quotation)}>
                                    <FileText className="mr-2 h-4 w-4" />
                                    导出
                                  </DropdownMenuItem>
                                  {quotation.status === 'confirmed' && (
                                    <DropdownMenuItem onClick={() => handleAction('convert', quotation)}>
                                      <ShoppingCart className="mr-2 h-4 w-4" />
                                      转换为订单
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleAction('delete', quotation)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    删除
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}

                        {quotations.length === 0 && (
                          <tr>
                            <td colSpan={headCells.length} className="px-4 py-8 text-center text-muted-foreground">
                              未找到报价单
                            </td>
                          </tr>
                        )}

                      </tbody>
                    </table>
                  </div>

                  {pagination.total_pages > 1 && (
                    <div className="flex justify-center py-4 border-t">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                        >
                          上一页
                        </Button>
                        <span className="text-sm text-muted-foreground px-4">
                          第 {pagination.page} / {pagination.total_pages} 页
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= pagination.total_pages}
                        >
                          下一页
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <p className="mt-4 text-sm text-muted-foreground">
            正在显示 {quotations.length} / {pagination.total_count} 条报价单
          </p>

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialog.open}
            onOpenChange={(open) => !open && setConfirmDialog({ open: false, quotation: null, action: '' })}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {confirmDialog.action === 'delete' ? '删除报价单' : '将报价单转换为销售订单'}
                </DialogTitle>
                <DialogDescription>
                  {confirmDialog.action === 'delete'
                    ? `确定要删除报价单 ${confirmDialog.quotation?.quotation_id} 吗？此操作不可撤销。`
                    : `确定要将报价单 ${confirmDialog.quotation?.quotation_id} 转换为销售订单吗？`
                  }
                </DialogDescription>
              </DialogHeader>
              {confirmDialog.action === 'convert' && (
                <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-md">
                  <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-warning font-medium">
                    注意：转换成功后，原报价单将被删除。所有数据将保留在新创建的销售合同中。
                  </p>
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialog({ open: false, quotation: null, action: '' })}
                >
                  取消
                </Button>
                <Button
                  variant={confirmDialog.action === 'delete' ? 'destructive' : 'default'}
                  onClick={handleConfirmAction}
                >
                  {confirmDialog.action === 'delete' ? '删除' : '转换'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </Layout>
        <TemplateSelector
          open={templateSelectorOpen}
          onClose={handleTemplateSelectorClose}
          onSelect={handleTemplateSelected}
          entityType="quotation"
          title="选择导出模板"
          allowManagement={true}
        />

        <SalesImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          mode="quotation"
          onImportComplete={() => {
            loadQuotations();
          }}
        />
      </>
    );
  };

export default QuotationPage;
